import React, { useState, useRef } from 'react';
import { TextField, InputAdornment, IconButton, Box } from '@mui/material';
import { Visibility, VisibilityOff, CalendarMonth } from '@mui/icons-material';

export interface TextFieldComponentProps {
  label: string;
  value: string | number | null | undefined;
  onChange: (value: string) => void;
  error?: string;
  type?: string;
  fullWidth?: boolean;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  multiline?: boolean;
  rows?: number;
  maxRows?: number;
  size?: 'small' | 'medium';
  variant?: 'outlined' | 'filled' | 'standard';
  autoComplete?: string;
  startIcon?: React.ReactNode;
  endIcon?: React.ReactNode;
  showPasswordToggle?: boolean;
}

/**
 * TextFieldComponent - Reusable Text Input Component
 * 
 * A standardized text field component that provides:
 * - Consistent styling and behavior across the application
 * - Built-in error handling and display
 * - Support for various input types (text, email, password, etc.)
 * - Flexible sizing and layout options
 * - Multiline support for text areas
 * - Icon support (start and end icons)
 * - Password visibility toggle for password fields
 */
export const TextFieldComponent: React.FC<TextFieldComponentProps> = ({
  label,
  value,
  onChange,
  error,
  type = 'text',
  fullWidth = true,
  placeholder,
  disabled = false,
  required = false,
  multiline = false,
  rows,
  maxRows,
  size = 'medium',
  variant = 'outlined',
  autoComplete,
  startIcon,
  endIcon,
  showPasswordToggle = false,
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const dateInputRef = useRef<HTMLInputElement>(null);

  const handleTogglePassword = () => {
    setShowPassword(prev => !prev);
  };

  const actualType = showPasswordToggle ? (showPassword ? 'text' : 'password') : type;

  const getInputProps = () => {
    const props: any = {};
    
    if (startIcon || endIcon || showPasswordToggle) {
      props.startAdornment = startIcon ? (
        <InputAdornment position="start">
          {startIcon}
        </InputAdornment>
      ) : undefined;

      if (showPasswordToggle && type === 'password') {
        props.endAdornment = (
          <InputAdornment position="end">
            <IconButton
              aria-label="toggle password visibility"
              onClick={handleTogglePassword}
              disabled={disabled}
              edge="end"
              size="small"
            >
              {showPassword ? <VisibilityOff /> : <Visibility />}
            </IconButton>
          </InputAdornment>
        );
      } else if (endIcon) {
        props.endAdornment = (
          <InputAdornment position="end">
            {endIcon}
          </InputAdornment>
        );
      }
    }

    return props;
  };

  // Handle date type with native date picker + formatted display
  if (type === 'date') {
    // Format YYYY-MM-DD to dd/MM/yyyy for display
    const formatDisplayDate = (val: string | number | null | undefined): string => {
      if (!val) return '';
      const str = String(val);
      const parts = str.split('-');
      if (parts.length === 3) {
        return `${parts[2]}/${parts[1]}/${parts[0]}`;
      }
      return str;
    };

    const handleDateClick = () => {
      if (dateInputRef.current && !disabled) {
        try {
          dateInputRef.current.showPicker();
        } catch {
          dateInputRef.current.click();
        }
      }
    };

    const handleNativeDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange(e.target.value);
    };

    return (
      <Box sx={{ position: 'relative' }}>
        <TextField
          label={label}
          value={formatDisplayDate(value)}
          onClick={handleDateClick}
          error={!!error}
          helperText={error}
          fullWidth={fullWidth}
          disabled={disabled}
          required={required}
          size={size}
          variant={variant}
          InputProps={{
            readOnly: true,
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={handleDateClick} disabled={disabled} size="small">
                  <CalendarMonth />
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{
            "& .MuiInputLabel-root": {
              "&.Mui-focused": {
                color: "#2563eb",
              },
            },
            "& .MuiInputBase-root": {
              backgroundColor: "#f9fafb",
              borderRadius: "0.5rem",
              fontSize: "0.875rem",
              color: "#111827",
              cursor: "pointer",
            },
            "& .MuiOutlinedInput-root": {
              "& fieldset": {
                borderColor: error ? 'error.main' : "#d1d5db",
              },
              "&:hover fieldset": {
                borderColor: "#9ca3af",
              },
              "&.Mui-focused fieldset": {
                borderColor: "#2563eb",
                boxShadow: "0 0 0 1px #2563eb",
              },
              "& input": {
                cursor: "pointer",
              },
            },
          }}
        />
        <input
          ref={dateInputRef}
          type="date"
          value={String(value || '')}
          onChange={handleNativeDateChange}
          disabled={disabled}
          style={{
            position: 'absolute',
            opacity: 0,
            width: 0,
            height: 0,
            bottom: 0,
            left: 0,
            pointerEvents: 'none',
          }}
          tabIndex={-1}
        />
      </Box>
    );
  }

  return (
    <TextField
      label={label}
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      error={!!error}
      helperText={error}
      type={actualType}
      fullWidth={fullWidth}
      placeholder={placeholder}
      disabled={disabled}
      required={required}
      multiline={multiline}
      rows={rows}
      maxRows={maxRows}
      size={size}
      variant={variant}
      autoComplete={autoComplete}
      InputProps={getInputProps()}
      sx={{
        "& .MuiInputLabel-root": {
          // Center the label vertically
          transform: "translate(14px, 16px) scale(1)",
          "&.MuiInputLabel-shrink": {
            transform: "translate(14px, -9px) scale(0.75)",
          },
          "&.Mui-focused": {
            color: "#2563eb", // primary-600
          },
        },
        "& .MuiInputBase-root": {
          backgroundColor: "#f9fafb", // bg-gray-50
          borderRadius: "0.5rem",     // rounded-lg
          fontSize: "0.875rem",       // text-sm
          color: "#111827",           // text-gray-900
          padding: "0",               // reset internal
        },
        "& .MuiOutlinedInput-root": {
          "& fieldset": {
            borderColor: error ? 'error.main' : "#d1d5db",   // border-gray-300
          },
          "&:hover fieldset": {
            borderColor: "#9ca3af",   // slightly darker on hover
          },
          "&.Mui-focused fieldset": {
            borderColor: "#2563eb",   // primary-600
            boxShadow: "0 0 0 1px #2563eb", // focus:ring-primary-600
          },
          "& input": {
            padding: "0.625rem 0.75rem", // p-2.5
            height: "1.5rem", // Ensure consistent height
          },
        },
      }}
    />
  );
};

export default TextFieldComponent;